import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';

import type { AdditionalResource } from '../../../types/additionalResource';
import type { CustomizationType } from '../../../types/customizationTypes';

const i18n = defineMessages({
	buttonText: {
		id: 'audio.audio-briefing-popup.restart-button',
		defaultMessage: 'Play',
		description: 'Text in a button that will regenerate and play an audio briefing when clicked',
	},
});

export type RestartButtonProps = {
	fetchBriefing: () => void;
	isLoading: boolean;
	hasDiverged: boolean;
};

export type SourcesPanelRestartButtonProps = {
	fetchBriefing: () => void;
	currentAdditionalResources: AdditionalResource[];
	lastGeneratedAdditionalResources?: AdditionalResource[];
	isLoading: boolean;
};

export type CustomizePanelRestartButtonProps = {
	fetchBriefing: () => void;
	currentCustomization: CustomizationType;
	lastGeneratedCustomization?: CustomizationType;
	isLoading: boolean;
};

const RestartButton = ({ fetchBriefing, isLoading, hasDiverged }: RestartButtonProps) => {
	return (
		<Button
			testId="restart-briefing-button"
			shouldFitContainer
			appearance="primary"
			onClick={fetchBriefing}
			isLoading={isLoading}
			isDisabled={!hasDiverged}
		>
			<FormattedMessage {...i18n.buttonText} />
		</Button>
	);
};

export const SourcesPanelRestartButton = ({
	fetchBriefing,
	currentAdditionalResources,
	lastGeneratedAdditionalResources,
	isLoading,
}: SourcesPanelRestartButtonProps) => {
	const sourcesAreIdentical =
		currentAdditionalResources.length === lastGeneratedAdditionalResources?.length &&
		lastGeneratedAdditionalResources.every(
			(lastGeneratedResource, index) =>
				currentAdditionalResources?.[index].ari === lastGeneratedResource.ari,
		);

	return (
		<RestartButton
			fetchBriefing={fetchBriefing}
			isLoading={isLoading}
			hasDiverged={!sourcesAreIdentical}
		/>
	);
};

export const CustomizePanelRestartButton = ({
	fetchBriefing,
	currentCustomization,
	lastGeneratedCustomization,
	isLoading,
}: CustomizePanelRestartButtonProps) => {
	const hasDiverged =
		currentCustomization.tone !== lastGeneratedCustomization?.tone ||
		currentCustomization.length !== lastGeneratedCustomization?.length;

	return (
		<RestartButton fetchBriefing={fetchBriefing} isLoading={isLoading} hasDiverged={hasDiverged} />
	);
};
